import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine
} from "recharts";
import { 
  Calendar as CalendarIcon,
  ChevronRight,
  ChevronDown,
  Trash2,
  Edit,
  Check,
  CheckCircle,
  X,
  TrendingUp,
  TrendingDown,
  Search,
  Filter,
  Pencil,
  DollarSign,
  Medal,
  Clock,
  AlertCircle,
  RefreshCw,
  Gamepad2,
  Link2,
  Flag,
  Share2
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "./ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Switch } from "./ui/switch";
import { format } from "date-fns";
import PlayerGameView from "./PlayerGameView";
import { ShareableScorecard } from "./ShareableScorecard";
import { InProgressGameRow } from "./compete/InProgressGameRow";
import { useInProgressGames } from "./compete/useInProgressGames";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Chart dot renderer - moved outside component to avoid re-creation
const getChartDot = (getScoreColor) => (props) => {
  const { cx, cy, payload } = props;
  if (!cx || !cy) return null;
  return (
    <circle 
      key={`dot-${payload?.score}-${cx}`}
      cx={cx} 
      cy={cy} 
      r={5} 
      fill={getScoreColor(payload?.score)}
      stroke="white"
      strokeWidth={2}
    />
  );
};

const HistoryPage = ({ currentUser }) => {
  const navigate = useNavigate();
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [inlineDeleteConfirm, setInlineDeleteConfirm] = useState(null); // For inline delete within expanded card
  const [expandedScore, setExpandedScore] = useState(null);
  const [sortBy, setSortBy] = useState('date');
  const [showTrend, setShowTrend] = useState(false);
  const [showRounds, setShowRounds] = useState(true);
  const [showGames, setShowGames] = useState(false);
  const [editScore, setEditScore] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [editDate, setEditDate] = useState(null);
  const [inlineEditId, setInlineEditId] = useState(null); // Track which score is being edited inline
  const location = useLocation();
  
  // Share scorecard state
  const [showShareScorecard, setShowShareScorecard] = useState(false);
  const [shareScoreData, setShareScoreData] = useState(null);
  
  // Active Games State
  const [myGames, setMyGames] = useState([]);
  const [loadingMyGames, setLoadingMyGames] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);
  const [refreshingGames, setRefreshingGames] = useState(false);
  
  // Active Group Rounds State
  const [activeGroupRounds, setActiveGroupRounds] = useState([]);
  const [loadingGroupRounds, setLoadingGroupRounds] = useState(false);
  const recoverableRounds = useInProgressGames(currentUser?.user_id);

  // Calculate proper rounds count:
  // - 18-hole rounds = 1 round each
  // - Paired 9-hole rounds = 1 round (count only primary)
  // - Unpaired 9-hole rounds = don't count (waiting for pair)
  const roundsCount = useMemo(() => {
    const eighteenHoleCount = scores.filter(s => (s.holes_played || 18) === 18).length;
    const pairedPrimaryCount = scores.filter(s => s.holes_played === 9 && s.is_combined_primary).length;
    return eighteenHoleCount + pairedPrimaryCount;
  }, [scores]);

  // Count unpaired 9-hole rounds
  const unpairedNinesCount = useMemo(() => {
    return scores.filter(s => s.holes_played === 9 && !s.paired_with_score_id).length;
  }, [scores]);

  // Fetch scores
  useEffect(() => {
    const fetchScores = async () => {
      try {
        const response = await axios.get(`${API}/scores`);
        setScores(response.data);
        
        // Check for score query param to auto-expand
        const params = new URLSearchParams(location.search);
        const scoreId = params.get('score');
        if (scoreId) {
          setExpandedScore(scoreId);
          // Scroll to the score after a short delay
          setTimeout(() => {
            const element = document.getElementById(`score-${scoreId}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 300);
        }
        
        // Check for edit query param to auto-open edit modal
        const editId = params.get('edit');
        if (editId) {
          const scoreToEdit = response.data.find(s => s.score_id === editId);
          if (scoreToEdit) {
            // Clear the URL param
            navigate('/history', { replace: true });
            // Open edit modal
            setTimeout(() => openEditModal(scoreToEdit), 100);
          }
        }
      } catch (err) {
        toast.error("Failed to load scores");
      } finally {
        setLoading(false);
      }
    };
    fetchScores();
  }, [location.search]);

  // Fetch My Games
  const fetchMyGames = async (showToast = false) => {
    try {
      if (showToast) setRefreshingGames(true);
      const response = await axios.get(`${API}/games/my-games`);
      setMyGames(response.data.games || []);
      if (showToast) toast.success('Games refreshed');
    } catch (error) {
      console.error('Error fetching my games:', error);
    } finally {
      setLoadingMyGames(false);
      setRefreshingGames(false);
    }
  };

  // Fetch Active Group Rounds
  const fetchActiveGroupRounds = async () => {
    try {
      setLoadingGroupRounds(true);
      const response = await axios.get(`${API}/group-rounds/my-rounds`);
      const rounds = response.data?.rounds || response.data || [];
      // Filter to only active/in-progress rounds
      setActiveGroupRounds(rounds.filter(r => r.status === 'active' || r.status === 'in_progress'));
    } catch (error) {
      console.error('Error fetching group rounds:', error);
    } finally {
      setLoadingGroupRounds(false);
    }
  };

  useEffect(() => {
    fetchMyGames();
    fetchActiveGroupRounds();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700 border-green-200';
      case 'completed': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-amber-100 text-amber-700 border-amber-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <Clock className="w-3 h-3" />;
      case 'completed': return <CheckCircle className="w-3 h-3" />;
      default: return <AlertCircle className="w-3 h-3" />;
    }
  };

  // Prepare trend data (chronological order, last 10)
  // STRICTLY ONLY include 18-hole rounds - exclude ALL 9-hole rounds
  const trendData = [...scores]
    .filter(s => {
      if (!s.date) return false; // Filter out scores without dates
      
      // Get holes played - check multiple possible fields
      const holesPlayed = s.holes_played;
      
      // STRICTLY only include 18-hole rounds
      // Exclude anything that is explicitly 9 holes
      if (holesPlayed === 9) return false;
      if (s.holes_played === 9) return false;
      if (s.holes === 9) return false;
      
      // Only include if it's a full 18-hole round
      return holesPlayed === 18;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(-10)
    .map(s => {
      return {
        date: s.date,
        score: s.total_score,
        par: s.par ?? null,
        course: s.course_name
      };
    });

  // Sort scores based on selected option
  const sortedScores = useMemo(() => {
    // For "active" filter, we don't show regular scores - we show active group rounds
    if (sortBy === 'active') {
      return [];
    }
    
    return [...scores].sort((a, b) => {
      if (sortBy === 'best') {
        return (a.total_score - a.par) - (b.total_score - b.par);
      } else if (sortBy === 'worst') {
        return (b.total_score - b.par) - (a.total_score - a.par);
      }
      // For date sorting, put null dates at the end
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(b.date) - new Date(a.date);
    });
  }, [scores, sortBy]);

  // Calculate rank for each score (1 = best)
  const scoreRanks = [...scores]
    .sort((a, b) => (a.total_score - a.par) - (b.total_score - b.par))
    .reduce((acc, score, index) => {
      acc[score.score_id] = index + 1;
      return acc;
    }, {});

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    
    setDeleting(true);
    try {
      if (deleteConfirm.type === 'group_round') {
        // Delete group round
        await axios.delete(`${API}/group-rounds/${deleteConfirm.round_id}`);
        setActiveGroupRounds(prev => prev.filter(r => r.round_id !== deleteConfirm.round_id));
        toast.success("Group round deleted successfully");
      } else {
        // Delete score
        await axios.delete(`${API}/scores/${deleteConfirm.score_id}`);
        setScores(scores.filter(s => s.score_id !== deleteConfirm.score_id));
        toast.success("Score deleted successfully");
      }
      setDeleteConfirm(null);
    } catch (error) {
      toast.error(deleteConfirm.type === 'group_round' ? "Failed to delete group round" : "Failed to delete score");
    } finally {
      setDeleting(false);
    }
  };

  const openEditModal = (score) => {
    // Use inline editing instead of modal
    setInlineEditId(score.score_id);
    setEditScore(score);
    setEditForm({
      course_name: score.course_name,
      date: score.date,
      total_score: score.total_score,
      front_9: score.front_9 || '',
      back_9: score.back_9 || '',
      par: score.par ?? '',
      notes: score.notes || '',
      verified: score.verified || false,
      hole_scores: score.hole_scores || Array(score.holes_played === 9 ? 9 : 18).fill(null)
    });
    setEditDate(score.date ? new Date(score.date) : new Date());
    // Make sure the score is expanded
    setExpandedScore(score.score_id);
  };

  const handleEditSave = async () => {
    if (!editScore) return;
    setSaving(true);
    try {
      const updateData = {
        course_name: editForm.course_name,
        date: editForm.date,
        total_score: Number(editForm.total_score),
        front_9: editForm.front_9 ? parseInt(editForm.front_9) : null,
        back_9: editForm.back_9 ? parseInt(editForm.back_9) : null,
        par: Number(editForm.par),
        notes: editForm.notes || null,
        verified: editForm.verified,
        hole_scores: editForm.hole_scores.some(h => h !== null && h !== '') 
          ? editForm.hole_scores.map(h => h !== null && h !== '' ? Number(h) : null)
          : null
      };
      
      const response = await axios.put(`${API}/scores/${editScore.score_id}`, updateData);
      setScores(scores.map(s => s.score_id === editScore.score_id ? response.data : s));
      toast.success("Score updated successfully");
      setEditScore(null);
      setInlineEditId(null); // Clear inline edit state
    } catch (error) {
      console.error("Error updating score:", error);
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : "Check the round par, hole scores, and total before saving");
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyToggle = async (score) => {
    try {
      const newVerified = !score.verified;
      const response = await axios.put(`${API}/scores/${score.score_id}`, { verified: newVerified });
      setScores(scores.map(s => s.score_id === score.score_id ? response.data : s));
      toast.success(newVerified ? "Score marked as verified" : "Verification removed");
    } catch (error) {
      toast.error("Failed to update verification status");
    }
  };

  // Open share scorecard modal for a specific round
  const handleShareRound = (score) => {
    // Prepare score data for sharecard
    const shareData = {
      score_id: score.score_id,
      course_id: score.course_id,
      course_name: score.course_name,
      date: score.date,
      total_score: score.total_score,
      par: score.par || 72,
      front_9: score.front_9,
      back_9: score.back_9,
      holes_played: score.holes_played || 18,
      hole_scores: score.hole_scores 
        ? score.hole_scores.map((s, i) => ({ hole: i + 1, score: s, par: 4 }))
        : [],
      course_pars: score.hole_pars || []
    };
    setShareScoreData(shareData);
    setShowShareScorecard(true);
  };

  // Handle sharing scorecard to feed
  const handleShareToFeed = async (imageData, scoreData) => {
    try {
      const relativeToPar = scoreData.total_score - scoreData.par;
      const relativeString = relativeToPar === 0 ? 'E' : (relativeToPar > 0 ? `+${relativeToPar}` : relativeToPar);
      
      const postContent = `Just finished a round at ${scoreData.course_name}!\n\nScore: ${scoreData.total_score} (${relativeString})\n\n#ParSeeker #GolfLife`;
      
      await axios.post(`${API}/posts`, {
        content: postContent,
        post_type: 'round_update',
        image_url: imageData,
        visibility: 'public'
      });
      
      toast.success("Scorecard shared to feed!");
      setShowShareScorecard(false);
      navigate('/home');
    } catch (error) {
      console.error('Error sharing to feed:', error);
      toast.error("Failed to share scorecard");
    }
  };

  // Get color for score based on percentile thresholds
  const getScoreColor = (score) => {
    if (score >= 100) return '#dc2626'; // Red - above 100
    if (score >= 90) return '#f5f5f4'; // White-ish - 90-99
    return '#2563eb'; // Blue - below 90
  };

  // Get stroke color for trend line based on latest score
  const getTrendStrokeColor = () => {
    if (trendData.length === 0) return '#2563eb';
    const latestScore = trendData[trendData.length - 1]?.score || 100;
    return getScoreColor(latestScore);
  };

  const getScoreClass = (score, par) => {
    const diff = score - par;
    if (diff < 0) return 'score-under';
    if (diff === 0) return 'score-par';
    if (diff <= 5) return 'score-over';
    return 'score-bad';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="dark-spinner" />
      </div>
    );
  }

  // If viewing a specific game, show PlayerGameView
  if (selectedGame) {
    return (
      <div className="dark-page max-w-4xl mx-auto animate-fade-in pt-2 px-4">
        <PlayerGameView 
          game={selectedGame}
          onBack={() => setSelectedGame(null)}
          currentUserId={currentUser?.user_id}
        />
      </div>
    );
  }

  return (
    <div className="dark-page max-w-4xl mx-auto animate-fade-in p-4" data-testid="history-page">
      {/* Delete Confirmation Modal - Centered and scrollable */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="dark-card w-full max-w-sm animate-fade-in my-auto">
            <div className="text-center p-4">
              <div className="w-14 h-14 bg-[var(--ps-error)]/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Trash2 className="w-7 h-7 text-[var(--ps-error)]" />
              </div>
              <h3 className="text-lg font-bold text-[var(--ps-text-primary)]">
                {deleteConfirm.type === 'group_round' ? 'DELETE GROUP ROUND?' : 'DELETE SCORE?'}
              </h3>
              <p className="text-sm text-[var(--ps-text-secondary)] mt-1">
                This action cannot be undone.
              </p>
            </div>
            <div className="space-y-3 px-4 pb-4">
              {/* Details - Compact */}
              <div className="bg-[var(--ps-bg-elevated)] rounded-lg p-3">
                <p className="font-medium text-[var(--ps-text-primary)] text-sm">{deleteConfirm.course_name}</p>
                <p className="text-xs text-[var(--ps-text-secondary)]">{deleteConfirm.date ? format(new Date(deleteConfirm.date), 'MMM d, yyyy') : 'No date'}</p>
                {deleteConfirm.type === 'group_round' ? (
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-bold text-[var(--ps-text-primary)]">{deleteConfirm.players?.length || 0} players</span>
                    <span className="text-xs text-[var(--ps-text-secondary)]">• {deleteConfirm.status || 'active'}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-bold text-[var(--ps-text-primary)]">Score: {deleteConfirm.total_score}</span>
                    <span className="text-xs text-[var(--ps-text-secondary)]">
                      ({deleteConfirm.total_score - deleteConfirm.par >= 0 ? '+' : ''}{deleteConfirm.total_score - deleteConfirm.par})
                    </span>
                  </div>
                )}
              </div>
              
              {deleteConfirm.type !== 'group_round' && (
                <div className="bg-[var(--ps-warning)]/10 border border-[var(--ps-warning)]/30 rounded-lg p-2.5">
                  <p className="text-xs text-[var(--ps-warning)]">
                    <strong>Note:</strong> This may affect your handicap.
                  </p>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <Button 
                  variant="outline" 
                  onClick={() => setDeleteConfirm(null)}
                  className="dark-btn-secondary flex-1 h-10"
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleDeleteConfirm}
                  className="flex-1 h-10"
                  style={{backgroundColor: 'var(--ps-error)'}}
                  disabled={deleting}
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? (
                    <div className="dark-spinner w-4 h-4" />
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-1.5" />
                      Delete
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section Selection - Dark Theme Boxes: Games, Rounds, Trends */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {/* Active Games Box - First */}
        <button
          onClick={() => { setShowGames(true); setShowRounds(false); setShowTrend(false); }}
          className={`relative p-4 rounded-xl border-2 transition-all text-center ${
            showGames 
              ? 'border-[var(--ps-warning)] bg-[var(--ps-warning)]/10' 
              : 'border-[var(--ps-border)] bg-[var(--ps-bg-surface)] hover:border-[var(--ps-warning)]/50'
          }`}
          data-testid="show-games-btn"
        >
          <div className={`text-2xl font-black ${showGames ? 'text-[var(--ps-warning)]' : 'text-[var(--ps-text-muted)]'}`}>
            {myGames.filter(g => g.status === 'active' || g.status === 'setup').length}
          </div>
          <div className={`text-xs font-bold uppercase tracking-wide mt-1 ${showGames ? 'text-[var(--ps-warning)]' : 'text-[var(--ps-text-secondary)]'}`}>
            Games
          </div>
          {showGames && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-[var(--ps-warning)] rounded-full" />
          )}
        </button>

        {/* Recent Rounds Box - Second */}
        <button
          onClick={() => { setShowRounds(true); setShowTrend(false); setShowGames(false); }}
          className={`relative p-4 rounded-xl border-2 transition-all text-center ${
            showRounds 
              ? 'border-[var(--ps-primary)] bg-[var(--ps-primary)]/10' 
              : 'border-[var(--ps-border)] bg-[var(--ps-bg-surface)] hover:border-[var(--ps-primary)]/50'
          }`}
          data-testid="show-rounds-btn"
        >
          <div className={`text-2xl font-black ${showRounds ? 'text-[var(--ps-primary)]' : 'text-[var(--ps-text-muted)]'}`}>
            {roundsCount}
          </div>
          <div className={`text-xs font-bold uppercase tracking-wide mt-1 ${showRounds ? 'text-[var(--ps-primary)]' : 'text-[var(--ps-text-secondary)]'}`}>
            Rounds
          </div>
          {unpairedNinesCount > 0 && (
            <div className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-[var(--ps-warning)] text-white text-[10px] font-bold rounded-full">
              +{unpairedNinesCount}
            </div>
          )}
          {showRounds && unpairedNinesCount === 0 && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-[var(--ps-primary)] rounded-full" />
          )}
        </button>

        {/* Score Trend Box - Third */}
        <button
          onClick={() => { setShowTrend(true); setShowRounds(false); setShowGames(false); }}
          className={`relative p-4 rounded-xl border-2 transition-all text-center ${
            showTrend 
              ? 'border-[var(--ps-info)] bg-[var(--ps-info)]/10' 
              : 'border-[var(--ps-border)] bg-[var(--ps-bg-surface)] hover:border-[var(--ps-info)]/50'
          }`}
          data-testid="show-trend-btn"
        >
          <TrendingUp className={`w-6 h-6 mx-auto ${showTrend ? 'text-[var(--ps-info)]' : 'text-[var(--ps-text-muted)]'}`} />
          <div className={`text-xs font-bold uppercase tracking-wide mt-1 ${showTrend ? 'text-[var(--ps-info)]' : 'text-[var(--ps-text-secondary)]'}`}>
            Trend
          </div>
          {showTrend && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-[var(--ps-info)] rounded-full" />
          )}
        </button>
      </div>

      {/* ACTIVE GAMES SECTION */}
      {showGames && (
        <div className="space-y-3 animate-fade-in">
          {/* Header with refresh */}
          <div className="flex items-center justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchMyGames(true)}
              disabled={refreshingGames}
              data-testid="refresh-games-btn"
              className="text-stone-500"
            >
              <RefreshCw className={`w-4 h-4 ${refreshingGames ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {loadingMyGames ? (
            <div className="flex justify-center py-8">
              <div className="dark-spinner w-8 h-8" />
            </div>
          ) : myGames.length === 0 ? (
            <div className="bg-[var(--ps-bg-surface)] border-2 border-dashed border-[var(--ps-border)] rounded-xl p-8 text-center">
              <Gamepad2 className="w-12 h-12 mx-auto text-[var(--ps-text-muted)] mb-3" />
              <p className="text-[var(--ps-text-primary)] font-medium">No active games</p>
              <p className="text-[var(--ps-text-secondary)] text-sm mt-1">Start a new game from Games menu</p>
              <button
                onClick={() => navigate('/tournaments')}
                className="mt-4 px-4 py-2 bg-[var(--ps-warning)] text-white rounded-xl font-medium hover:opacity-90 transition-opacity"
              >
                Create New Game
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {myGames.map((game) => (
                <div 
                  key={game.event_id}
                  onClick={() => setSelectedGame(game)}
                  className="flex items-center gap-3 p-4 bg-[var(--ps-bg-surface)] rounded-xl border border-[var(--ps-border)] cursor-pointer hover:border-[var(--ps-warning)] transition-all"
                  data-testid={`game-${game.event_id}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    game.game_type === 'skins' ? 'bg-[var(--ps-warning)]/20' : 'bg-[var(--ps-info)]/20'
                  }`}>
                    {game.game_type === 'skins' ? (
                      <DollarSign className="w-6 h-6 text-[var(--ps-warning)]" />
                    ) : (
                      <Medal className="w-6 h-6 text-[var(--ps-info)]" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-[var(--ps-text-primary)] truncate">{game.name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold border flex items-center gap-1 ${getStatusColor(game.status)}`}>
                        {getStatusIcon(game.status)}
                        {game.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[var(--ps-text-secondary)] mt-0.5">
                      <span className="font-medium">{game.game_type === 'skins' ? 'Skins' : 'Stroke Play'}</span>
                      <span className="text-[var(--ps-text-muted)]">•</span>
                      <span>{game.players?.length || 0} players</span>
                      {game.pending_contests > 0 && (
                        <>
                          <span className="text-[var(--ps-text-muted)]">•</span>
                          <span className="text-[var(--ps-warning)] font-bold">{game.pending_contests} contest{game.pending_contests > 1 ? 's' : ''}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-[var(--ps-text-muted)]" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SCORE TREND SECTION */}
      {showTrend && scores.length > 1 && (
        <div className="animate-fade-in">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  Score Trend
                </CardTitle>
                <span className="text-sm font-medium text-[var(--ps-text-secondary)]">
                  {currentUser?.name || currentUser?.username || 'Player'} • {new Date().getFullYear()}
                </span>
              </div>
              <CardDescription>Your recent 18-hole performance over time</CardDescription>
            </CardHeader>
            <CardContent>
              {trendData.length < 2 ? (
                <div className="h-64 flex flex-col items-center justify-center text-center">
                  <TrendingUp className="w-12 h-12 text-[var(--ps-text-muted)] mb-3" />
                  <p className="text-[var(--ps-text-primary)] font-medium">Not enough data for trend</p>
                  <p className="text-[var(--ps-text-secondary)] text-sm mt-1">
                    Play at least 2 full 18-hole rounds (or paired 9-hole rounds) to see your trend
                  </p>
                </div>
              ) : (
              <>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => value ? format(new Date(value), 'MMM d') : ''}
                      stroke="#a8a29e"
                      fontSize={12}
                    />
                    <YAxis 
                      domain={[70, 'auto']}
                      stroke="#a8a29e"
                      fontSize={12}
                      ticks={[80, 90, 100, 110]}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: 'white', 
                        border: '1px solid #e7e5e4',
                        borderRadius: '8px'
                      }}
                      formatter={(value, name, props) => [
                        <span key="score">
                          {value} ({value - props.payload.par >= 0 ? '+' : ''}{value - props.payload.par})
                          {props.payload.isPaired9Hole && <span className="text-xs text-gray-500 ml-1">(9+9)</span>}
                        </span>,
                        'Score'
                      ]}
                      labelFormatter={(label, payload) => {
                        try {
                          if (payload && payload[0]) {
                            const isPaired = payload[0].payload.isPaired9Hole;
                            const courseName = payload[0].payload.course;
                            return `${format(new Date(label), 'MMM d, yyyy')} - ${courseName}${isPaired ? ' (Combined 9s)' : ''}`;
                          }
                          return label ? format(new Date(label), 'MMM d, yyyy') : '';
                        } catch {
                          return '';
                        }
                      }}
                    />
                    {/* Reference lines for skill levels */}
                    <ReferenceLine y={100} stroke="#dc2626" strokeDasharray="3 3" label={{ value: '100 (55%)', position: 'right', fill: '#dc2626', fontSize: 10 }} />
                    <ReferenceLine y={90} stroke="#a8a29e" strokeDasharray="3 3" label={{ value: '90 (26%)', position: 'right', fill: '#a8a29e', fontSize: 10 }} />
                    <ReferenceLine y={80} stroke="#2563eb" strokeDasharray="3 3" label={{ value: '80 (5%)', position: 'right', fill: '#2563eb', fontSize: 10 }} />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      stroke={getTrendStrokeColor()}
                      strokeWidth={3}
                      dot={getChartDot(getScoreColor)}
                      activeDot={{ r: 7, fill: '#3b82f6' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {/* Legend */}
              <div className="flex items-center justify-center gap-6 mt-4 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-600" />
                  <span className="text-stone-600">100+ (55% of golfers)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-stone-300" />
                  <span className="text-stone-600">90-99 (26% of golfers)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-600" />
                  <span className="text-stone-600">Under 80 (5% of golfers)</span>
                </div>
              </div>
              {/* 18-hole only note */}
              <div className="mt-4 text-center">
                <p className="text-xs text-stone-500 italic">
                  * Only full 18-hole rounds are included in this trend analysis
                </p>
              </div>
              </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {showTrend && scores.length <= 1 && (
        <div className="bg-[var(--ps-bg-surface)] border-2 border-dashed border-[var(--ps-border)] rounded-xl p-8 text-center">
          <TrendingUp className="w-12 h-12 mx-auto text-[var(--ps-text-muted)] mb-3" />
          <p className="text-[var(--ps-text-primary)] font-medium">Not enough data</p>
          <p className="text-[var(--ps-text-secondary)] text-sm mt-1">Play at least 2 rounds to see your trend</p>
        </div>
      )}

      {/* RECENT ROUNDS SECTION */}
      {showRounds && (
        <div className="animate-fade-in">
          {/* Sort Options - Dark Theme */}
          {(scores.length > 0 || recoverableRounds.loading || recoverableRounds.games.length > 0 || recoverableRounds.errors.length > 0) && (
            <div className="flex justify-center gap-1.5 mb-4 flex-wrap">
              <button
                onClick={() => setSortBy('active')}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                  sortBy === 'active' 
                    ? 'bg-green-500 text-white shadow-[0_0_12px_rgba(34,197,94,0.5)]' 
                    : 'bg-[var(--ps-bg-surface)] text-[var(--ps-text-secondary)] border border-[var(--ps-border)] hover:border-green-500'
                }`}
                data-testid="history-active-rounds-filter"
              >
                Active{recoverableRounds.games.length ? ` (${recoverableRounds.games.length})` : ''}
              </button>
              <button
                onClick={() => setSortBy('date')}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                  sortBy === 'date' 
                    ? 'bg-[var(--ps-primary)] text-white shadow-[0_0_12px_rgba(74,222,128,0.5)]' 
                    : 'bg-[var(--ps-bg-surface)] text-[var(--ps-text-secondary)] border border-[var(--ps-border)] hover:border-[var(--ps-primary)]'
                }`}
              >
                Recent
              </button>
              <button
                onClick={() => setSortBy('best')}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                  sortBy === 'best' 
                    ? 'bg-[var(--ps-primary)] text-white shadow-[0_0_12px_rgba(74,222,128,0.5)]' 
                    : 'bg-[var(--ps-bg-surface)] text-[var(--ps-text-secondary)] border border-[var(--ps-border)] hover:border-[var(--ps-primary)]'
                }`}
              >
                Best
              </button>
              <button
                onClick={() => setSortBy('worst')}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                  sortBy === 'worst' 
                    ? 'bg-[var(--ps-error)] text-white shadow-[0_0_12px_rgba(239,68,68,0.5)]' 
                    : 'bg-[var(--ps-bg-surface)] text-[var(--ps-text-secondary)] border border-[var(--ps-border)] hover:border-[var(--ps-error)]'
                }`}
              >
                Worst
              </button>
            </div>
          )}

          {/* Every recoverable round - when "Active" filter is selected */}
          {sortBy === 'active' && (
            <div className="space-y-3" data-testid="history-active-rounds">
              {recoverableRounds.loading ? (
                <div className="flex justify-center py-8">
                  <div className="dark-spinner w-8 h-8" role="status" aria-label="Loading active rounds" data-testid="history-active-rounds-loading" />
                </div>
              ) : recoverableRounds.games.length === 0 && recoverableRounds.errors.length === 0 ? (
                <div className="bg-[var(--ps-bg-surface)] border-2 border-dashed border-[var(--ps-border)] rounded-xl p-8 text-center">
                  <Flag className="w-12 h-12 mx-auto text-[var(--ps-text-muted)] mb-3" />
                  <h3 className="text-lg font-bold text-[var(--ps-text-primary)] mb-2">No Active Rounds</h3>
                  <p className="text-[var(--ps-text-secondary)] mb-4">Start a round to keep it available here for recovery.</p>
                  <button 
                    onClick={() => navigate('/add-score')}
                    className="px-4 py-2 bg-green-500 text-white rounded-xl font-bold hover:opacity-90 transition-opacity"
                    data-testid="history-start-round"
                  >
                    Start Round
                  </button>
                </div>
              ) : (
                <>
                  {recoverableRounds.errors.length > 0 && <div role="alert" className="text-sm border border-[var(--ps-warning)]/40 bg-[var(--ps-warning)]/10 rounded-lg p-3" data-testid="history-active-rounds-error">
                    Some active rounds could not be loaded. <button type="button" className="underline font-semibold" onClick={recoverableRounds.refresh} data-testid="history-active-rounds-retry">Try again</button>
                  </div>}
                  {recoverableRounds.games.map(round => <InProgressGameRow key={round.key} game={{ ...round, isOwner: false }} onRemove={() => {}} />)}
                </>
              )}
            </div>
          )}

          {/* Regular Scores List - hide when "Active" filter is selected */}
          {sortBy !== 'active' && scores.length === 0 ? (
            <div className="bg-[var(--ps-bg-surface)] border-2 border-dashed border-[var(--ps-border)] rounded-xl p-8 text-center">
              <CalendarIcon className="w-12 h-12 mx-auto text-[var(--ps-text-muted)] mb-3" />
              <h3 className="text-lg font-bold text-[var(--ps-text-primary)] mb-2">No scores yet</h3>
              <p className="text-[var(--ps-text-secondary)] mb-4">Start tracking your rounds to see your history here.</p>
              <button 
                onClick={() => navigate('/add-score')}
                className="text-[var(--ps-primary)] font-bold hover:underline"
              >
                Add Your First Score →
              </button>
            </div>
          ) : sortBy !== 'active' && (
            <div className="grid grid-cols-1 gap-3">
              {sortedScores.map((score) => {
                const rank = scoreRanks[score.score_id];
                const isExpanded = expandedScore === score.score_id;
                const isBest = rank === 1;
                const isWorst = rank === scores.length && scores.length > 1;
                const vsPar = score.total_score - (score.par || 72);
                
                return (
                  <div 
                    key={score.score_id} 
                    id={`score-${score.score_id}`} 
                    data-testid={`history-row-${score.score_id}`}
                    data-score-id={score.score_id}
                    className={`bg-[var(--ps-bg-surface)] rounded-xl overflow-hidden border transition-all cursor-pointer ${
                      isExpanded 
                        ? 'border-[var(--ps-primary)] shadow-lg' 
                        : 'border-[var(--ps-border)] hover:border-[var(--ps-primary)]/50'
                    } ${score.paired_with_score_id ? 'ring-1 ring-[var(--ps-primary)]/30' : ''}`}
                    onClick={() => setExpandedScore(isExpanded ? null : score.score_id)}
                  >
                    {/* Collapsed View - Card Style like Pro Shop */}
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        {/* Course Image Placeholder */}
                        <div className="w-16 h-16 rounded-xl bg-[var(--ps-bg-elevated)] flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {score.course_image ? (
                            <img src={score.course_image} alt={score.course_name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="text-2xl">⛳</div>
                          )}
                        </div>
                        
                        {/* Course Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-[var(--ps-text-primary)] truncate">{score.course_name}</h3>
                              <p className="text-sm text-[var(--ps-text-secondary)]">
                                {score.date ? format(new Date(score.date), 'MMM d, yyyy') : 'No date'}
                              </p>
                            </div>
                            
                            {/* Score Badge */}
                            <div className={`px-3 py-1.5 rounded-full font-bold text-sm flex items-center gap-1 ${
                              vsPar < 0 ? 'bg-[var(--ps-success)]/20 text-[var(--ps-success)]' :
                              vsPar === 0 ? 'bg-[var(--ps-info)]/20 text-[var(--ps-info)]' :
                              'bg-[var(--ps-error)]/20 text-[var(--ps-error)]'
                            }`}>
                              {score.total_score}
                              <span className="text-xs opacity-80">
                                ({vsPar >= 0 ? '+' : ''}{vsPar})
                              </span>
                            </div>
                          </div>
                          
                          {/* Badges Row */}
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {(score.holes_played === 9 || score.holes === 9) && (
                              <>
                                <span className={`px-2 py-0.5 text-xs font-bold rounded-full flex items-center gap-1 ${
                                  score.paired_with_score_id 
                                    ? 'bg-[var(--ps-primary)]/20 text-[var(--ps-primary)]'
                                    : 'bg-[var(--ps-warning)]/20 text-[var(--ps-warning)]'
                                }`}>
                                  {score.paired_with_score_id && <Link2 className="w-3 h-3" />}
                                  {score.starting_nine === 'front' ? 'FRONT 9' : score.starting_nine === 'back' ? 'BACK 9' : '9 HOLES'}
                                </span>
                                {score.paired_with_score_id && score.combined_differential !== null && (
                                  <span className="px-2 py-0.5 bg-[var(--ps-info)]/20 text-[var(--ps-info)] text-xs font-bold rounded-full">
                                    DIFF: {score.combined_differential >= 0 ? '+' : ''}{score.combined_differential}
                                  </span>
                                )}
                              </>
                            )}
                            {score.verified && (
                              <span className="px-2 py-0.5 bg-[var(--ps-success)]/20 text-[var(--ps-success)] text-xs font-bold rounded-full flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                VERIFIED
                              </span>
                            )}
                            {isBest && (
                              <span className="px-2 py-0.5 bg-[var(--ps-success)]/20 text-[var(--ps-success)] text-xs font-bold rounded-full">
                                BEST
                              </span>
                            )}
                            {isWorst && (
                              <span className="px-2 py-0.5 bg-[var(--ps-error)]/20 text-[var(--ps-error)] text-xs font-bold rounded-full">
                                WORST
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Expand Indicator */}
                        <ChevronDown className={`w-5 h-5 text-[var(--ps-text-muted)] transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
                      </div>
                      
                      {/* Tap to view hint + Quick Share */}
                      {!isExpanded && (
                        <div className="mt-2 flex items-center justify-between">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleShareRound(score); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--ps-primary)]/20 text-[var(--ps-primary)] rounded-lg text-xs font-medium hover:bg-[var(--ps-primary)]/30 transition-all"
                            data-testid={`share-quick-${score.score_id}`}
                          >
                            <Share2 className="w-3.5 h-3.5" />
                            Share
                          </button>
                          <span className="text-xs text-[var(--ps-text-muted)]">Tap to view details</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-[var(--ps-border)] animate-fade-in">
                        <div className="pt-4 space-y-4">
                          
                          {/* INLINE EDIT FORM - Shows when editing this score */}
                          {inlineEditId === score.score_id ? (
                            <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-600 rounded-xl p-4 space-y-4">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-bold text-[var(--ps-text-primary)] flex items-center gap-2">
                                  <Pencil className="w-4 h-4 text-blue-600" />
                                  Edit Round
                                </h4>
                                <button 
                                  onClick={(e) => { e.stopPropagation(); setInlineEditId(null); setEditScore(null); }}
                                  className="p-1 hover:bg-[var(--ps-bg-elevated)] rounded-lg"
                                >
                                  <X className="w-5 h-5 text-[var(--ps-text-muted)]" />
                                </button>
                              </div>
                              
                              {/* Date Picker */}
                              <div>
                                <Label className="text-xs text-[var(--ps-text-secondary)] mb-1.5 block">Date</Label>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <button 
                                      onClick={(e) => e.stopPropagation()}
                                      className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-[var(--ps-bg-elevated)] border border-[var(--ps-border)] rounded-lg text-sm w-full justify-start"
                                    >
                                      <CalendarIcon className="h-4 w-4 text-[var(--ps-text-muted)]" />
                                      {editDate ? format(editDate, 'MMM d, yyyy') : 'Pick date'}
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                      mode="single"
                                      selected={editDate}
                                      onSelect={(date) => {
                                        setEditDate(date);
                                        setEditForm({...editForm, date: date ? format(date, 'yyyy-MM-dd') : editForm.date});
                                      }}
                                      initialFocus
                                    />
                                  </PopoverContent>
                                </Popover>
                              </div>
                              
                              {/* Hole by Hole Grid */}
                              <div>
                                <Label className="text-xs text-[var(--ps-text-secondary)] mb-1.5 block">Hole by Hole Scores</Label>
                                <div className="space-y-2">
                                  <div className="grid grid-cols-9 gap-1">
                                    {[1,2,3,4,5,6,7,8,9].map((hole) => (
                                      <div key={hole} className="text-center">
                                        <div className="text-[10px] text-[var(--ps-text-muted)] mb-0.5">{hole}</div>
                                        <Input
                                          type="number"
                                          min="1"
                                          max="15"
                                          className="text-center text-xs p-1 h-8 w-full bg-white dark:bg-[var(--ps-bg-elevated)]"
                                          value={editForm.hole_scores?.[hole-1] ?? ''}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) => {
                                            const newHoles = [...(editForm.hole_scores || Array(18).fill(null))];
                                            newHoles[hole-1] = e.target.value ? parseInt(e.target.value) : null;
                                            setEditForm({...editForm, hole_scores: newHoles});
                                          }}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                  <div className="grid grid-cols-9 gap-1">
                                    {[10,11,12,13,14,15,16,17,18].map((hole) => (
                                      <div key={hole} className="text-center">
                                        <div className="text-[10px] text-[var(--ps-text-muted)] mb-0.5">{hole}</div>
                                        <Input
                                          type="number"
                                          min="1"
                                          max="15"
                                          className="text-center text-xs p-1 h-8 w-full bg-white dark:bg-[var(--ps-bg-elevated)]"
                                          value={editForm.hole_scores?.[hole-1] ?? ''}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) => {
                                            const newHoles = [...(editForm.hole_scores || Array(18).fill(null))];
                                            newHoles[hole-1] = e.target.value ? parseInt(e.target.value) : null;
                                            setEditForm({...editForm, hole_scores: newHoles});
                                          }}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Verified Toggle */}
                              <div className="flex items-center justify-between py-2 px-3 bg-white dark:bg-[var(--ps-bg-elevated)] rounded-lg" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center gap-2">
                                  <CheckCircle className={`w-4 h-4 ${editForm.verified ? 'text-green-600' : 'text-[var(--ps-text-muted)]'}`} />
                                  <span className="text-sm font-medium text-[var(--ps-text-primary)]">Verified</span>
                                </div>
                                <Switch
                                  checked={editForm.verified || false}
                                  onCheckedChange={(checked) => setEditForm({...editForm, verified: checked})}
                                />
                              </div>

                              {/* Action Buttons */}
                              <div className="flex gap-2 pt-2">
                                <Button 
                                  variant="outline" 
                                  onClick={(e) => { e.stopPropagation(); setInlineEditId(null); setEditScore(null); }}
                                  className="flex-1 h-10"
                                  disabled={saving}
                                >
                                  Cancel
                                </Button>
                                <Button 
                                  onClick={(e) => { e.stopPropagation(); handleEditSave(); }}
                                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-10"
                                  disabled={saving}
                                  data-testid="save-inline-edit-btn"
                                >
                                  {saving ? (
                                    <div className="spinner w-4 h-4 border-white border-t-transparent" />
                                  ) : (
                                    <>
                                      <Check className="w-4 h-4 mr-1" />
                                      Save
                                    </>
                                  )}
                                </Button>
                              </div>
                              
                              {/* Delete Button */}
                              <Button 
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Show inline delete confirmation instead of modal
                                  setInlineDeleteConfirm(score.score_id);
                                }}
                                className="w-full h-9 text-red-500 hover:text-red-600 hover:bg-red-50 text-sm"
                                disabled={saving}
                              >
                                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                                Delete Round
                              </Button>
                              
                              {/* Inline Delete Confirmation - Appears right here */}
                              {inlineDeleteConfirm === score.score_id && (
                                <div className="mt-3 bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-600 rounded-xl p-4 animate-fade-in">
                                  <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 bg-red-100 dark:bg-red-800/50 rounded-full flex items-center justify-center flex-shrink-0">
                                      <Trash2 className="w-5 h-5 text-red-600" />
                                    </div>
                                    <div>
                                      <h4 className="font-bold text-red-700 dark:text-red-400">Delete this round?</h4>
                                      <p className="text-xs text-red-600/80 dark:text-red-400/80">This cannot be undone</p>
                                    </div>
                                  </div>
                                  
                                  <div className="bg-white/80 dark:bg-[var(--ps-bg-elevated)] rounded-lg p-2.5 mb-3">
                                    <p className="font-medium text-[var(--ps-text-primary)] text-sm">{score.course_name}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="font-bold text-[var(--ps-text-primary)] text-sm">
                                        {score.total_score}
                                      </span>
                                      <span className="text-xs text-[var(--ps-text-secondary)]">
                                        ({score.total_score - score.par >= 0 ? '+' : ''}{score.total_score - score.par})
                                      </span>
                                      <span className="text-xs text-[var(--ps-text-muted)]">•</span>
                                      <span className="text-xs text-[var(--ps-text-secondary)]">
                                        {score.date ? format(new Date(score.date), 'MMM d, yyyy') : 'No date'}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 p-2 rounded-lg mb-3">
                                    ⚠️ Deleting may affect your handicap calculation
                                  </p>
                                  
                                  <div className="flex gap-2">
                                    <Button 
                                      variant="outline" 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setInlineDeleteConfirm(null);
                                      }}
                                      className="flex-1 h-9 text-sm"
                                      disabled={deleting}
                                    >
                                      Cancel
                                    </Button>
                                    <Button 
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        setDeleting(true);
                                        try {
                                          await axios.delete(`${API}/scores/${score.score_id}`);
                                          toast.success('Round deleted');
                                          setInlineDeleteConfirm(null);
                                          setInlineEditId(null);
                                          setEditScore(null);
                                          setExpandedScore(null);
                                          // Update scores list directly
                                          setScores(prev => prev.filter(s => s.score_id !== score.score_id));
                                        } catch (error) {
                                          console.error('Error deleting:', error);
                                          toast.error('Failed to delete round');
                                        } finally {
                                          setDeleting(false);
                                        }
                                      }}
                                      className="flex-1 h-9 bg-red-600 hover:bg-red-700 text-white text-sm"
                                      disabled={deleting}
                                    >
                                      {deleting ? (
                                        <div className="dark-spinner w-4 h-4" />
                                      ) : (
                                        <>
                                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                                          Delete
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <>
                          {/* 9-hole pairing status */}
                          {(score.holes_played === 9 || score.holes === 9) && (
                            <>
                              {score.paired_with_score_id ? (
                                <div className="bg-[var(--ps-primary)]/10 border border-[var(--ps-primary)]/30 rounded-lg p-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Link2 className="w-4 h-4 text-[var(--ps-primary)]" />
                                    <span className="text-sm font-semibold text-[var(--ps-primary)]">Combined for Handicap</span>
                                  </div>
                                  <p className="text-sm text-[var(--ps-text-secondary)]">
                                    This round is paired with another 9-hole round. Combined differential: 
                                    <span className="font-bold text-[var(--ps-text-primary)] ml-1">
                                      {score.combined_differential >= 0 ? '+' : ''}{score.combined_differential}
                                    </span>
                                  </p>
                                  {/* Link to partner round */}
                                  {scores.find(s => s.score_id === score.paired_with_score_id) && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedScore(score.paired_with_score_id);
                                        // Scroll to the paired round
                                        setTimeout(() => {
                                          document.querySelector(`[data-score-id="${score.paired_with_score_id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        }, 100);
                                      }}
                                      className="mt-2 text-xs text-[var(--ps-primary)] hover:underline flex items-center gap-1"
                                    >
                                      <Flag className="w-3 h-3" />
                                      View linked round →
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div className="bg-[var(--ps-warning)]/10 border border-[var(--ps-warning)]/30 rounded-lg p-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Clock className="w-4 h-4 text-[var(--ps-warning)]" />
                                    <span className="text-sm font-semibold text-[var(--ps-warning)]">Awaiting Pair</span>
                                  </div>
                                  <p className="text-sm text-[var(--ps-text-secondary)]">
                                    This 9-hole round is waiting to be paired with another 9-hole round. 
                                    Play another 9 to combine them for your handicap calculation.
                                  </p>
                                </div>
                              )}
                            </>
                          )}
                          
                          {/* Score Summary */}
                          <div className="grid grid-cols-3 gap-3 text-center">
                            <div className="bg-[var(--ps-bg-elevated)] rounded-xl p-3">
                              <p className="text-xs text-[var(--ps-text-muted)] uppercase tracking-wide">Total</p>
                              <p className="text-2xl font-black text-[var(--ps-text-primary)]">{score.total_score}</p>
                            </div>
                            <div className="bg-[var(--ps-bg-elevated)] rounded-xl p-3">
                              <p className="text-xs text-[var(--ps-text-muted)] uppercase tracking-wide">Par</p>
                              <p className="text-2xl font-black text-[var(--ps-text-primary)]">{score.par || 72}</p>
                            </div>
                            <div className="bg-[var(--ps-bg-elevated)] rounded-xl p-3">
                              <p className="text-xs text-[var(--ps-text-muted)] uppercase tracking-wide">vs Par</p>
                              <p className={`text-2xl font-black ${
                                vsPar < 0 ? 'text-[var(--ps-success)]' : 
                                vsPar === 0 ? 'text-[var(--ps-info)]' : 'text-[var(--ps-error)]'
                              }`}>
                                {vsPar >= 0 ? '+' : ''}{vsPar}
                              </p>
                            </div>
                          </div>

                          {/* Hole by Hole Scores */}
                          {score.hole_scores && score.hole_scores.some(h => h !== null) && (
                            <div className="space-y-3">
                              <p className="text-sm font-bold text-[var(--ps-text-primary)]">Hole by Hole</p>
                              <div className="grid grid-cols-9 gap-1.5">
                                {score.hole_scores.slice(0, 9).map((holeScore, idx) => (
                                  <div key={`hole-${idx + 1}`} className="text-center">
                                    <div className="text-xs text-[var(--ps-text-muted)] mb-1">{idx + 1}</div>
                                    <div className={`text-lg font-black rounded-lg py-2 ${
                                      holeScore === null ? 'text-[var(--ps-text-muted)] bg-transparent' :
                                      holeScore <= 3 ? 'bg-[var(--ps-success)]/20 text-[var(--ps-success)]' :
                                      holeScore === 4 ? 'bg-[var(--ps-info)]/20 text-[var(--ps-info)]' :
                                      holeScore === 5 ? 'bg-[var(--ps-warning)]/20 text-[var(--ps-warning)]' :
                                      'bg-[var(--ps-error)]/20 text-[var(--ps-error)]'
                                    }`}>
                                      {holeScore ?? '-'}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {score.hole_scores.length > 9 && (
                                <div className="grid grid-cols-9 gap-1.5">
                                  {score.hole_scores.slice(9, 18).map((holeScore, idx) => (
                                    <div key={`hole-${idx + 10}`} className="text-center">
                                      <div className="text-xs text-[var(--ps-text-muted)] mb-1">{idx + 10}</div>
                                      <div className={`text-lg font-black rounded-lg py-2 ${
                                        holeScore === null ? 'text-[var(--ps-text-muted)] bg-transparent' :
                                        holeScore <= 3 ? 'bg-[var(--ps-success)]/20 text-[var(--ps-success)]' :
                                        holeScore === 4 ? 'bg-[var(--ps-info)]/20 text-[var(--ps-info)]' :
                                        holeScore === 5 ? 'bg-[var(--ps-warning)]/20 text-[var(--ps-warning)]' :
                                        'bg-[var(--ps-error)]/20 text-[var(--ps-error)]'
                                      }`}>
                                        {holeScore ?? '-'}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Notes */}
                          {score.notes && (
                            <div className="bg-[var(--ps-bg-elevated)] rounded-xl p-3">
                              <p className="text-sm text-[var(--ps-text-secondary)] italic">&ldquo;{score.notes}&rdquo;</p>
                            </div>
                          )}

                          {/* Rank Info */}
                          <div className="text-center text-sm text-[var(--ps-text-muted)] pt-2 border-t border-[var(--ps-border)]">
                            Ranked #{rank} of {scores.length} rounds
                            {isBest && ' 🏆'}
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center justify-between gap-2 pt-3 border-t border-[var(--ps-border)]">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleVerifyToggle(score); }}
                              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                                score.verified 
                                  ? 'bg-[var(--ps-success)]/20 text-[var(--ps-success)]' 
                                  : 'bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-hover)]'
                              }`}
                              data-testid={`verify-toggle-${score.score_id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                              {score.verified ? 'Verified' : 'Verify'}
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleShareRound(score); }}
                              className="flex items-center gap-1.5 px-3 py-2 bg-[var(--ps-primary)]/20 text-[var(--ps-primary)] rounded-xl text-sm font-medium hover:bg-[var(--ps-primary)]/30 transition-all"
                              data-testid={`share-round-${score.score_id}`}
                            >
                              <Share2 className="w-4 h-4" />
                              Share
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); openEditModal(score); }}
                              className="flex items-center gap-1.5 px-3 py-2 bg-[var(--ps-info)] text-white rounded-xl text-sm font-medium hover:opacity-90 transition-all"
                              data-testid={`edit-expanded-${score.score_id}`}
                            >
                              <Pencil className="w-4 h-4" />
                              Edit
                            </button>
                          </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      
      {/* Shareable Scorecard Modal */}
      <ShareableScorecard
        isOpen={showShareScorecard}
        score={shareScoreData}
        user={currentUser}
        course={null}
        onClose={() => setShowShareScorecard(false)}
        onShareToFeed={handleShareToFeed}
        onEdit={openEditModal}
      />
    </div>
  );
};


export default HistoryPage;
